import {
  Button,
  Popconfirm,
  Popover,
  Space,
  TableProps,
  Tag,
  Select,
  message,
  Switch,
} from "antd";
import { CiTrash } from "react-icons/ci";
import { MdEdit } from "react-icons/md";
import { Link } from "react-router-dom";
import { ICategory } from "../../types/Category";
import { TbImageInPicture } from "react-icons/tb";
import { MdAttachFile } from "react-icons/md";
import { LinkPreview } from "../../Components/LinkPreview";
import { VscActivateBreakpoints } from "react-icons/vsc";
import { MdNewspaper } from "react-icons/md";
import {
  addToMainNews,
  publishArticle,
  removeFromMainNews,
  unpublishArticle,
} from "./action";
import { IUser } from "../../types/UserData";
export interface ArticleTableData {
  id: string;
  title: string;
  author: string;
  source_name: string;
  source_link: string;
  alt: string;
  image: string;
  category: ICategory;
  keywords: string[];
  description: string;
  createdAt: string;
  updatedAt: string;
  onMainNews: boolean;
  user: string;
  published: boolean;
}

export const ArticleTableColumns = (
  page: number,
  pageSize: number,
  handleDelete: (id: string) => void,
  handleUpdate: (id: string) => void,
  handleRefresh?: () => void
): TableProps<ArticleTableData>["columns"] => [
  {
    title: "Main Info",
    children: [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        render: (category: ICategory) => (
          <Popover
            content={
              <div className="flex flex-col gap-2">
                <div className="flex flex-row gap-2">
                  {category.Seo.Keywords.map((keyword: string) => (
                    <Tag color="blue" key={keyword}>
                      {keyword}
                    </Tag>
                  ))}
                </div>
                <p>
                  <b>Description:</b> {category.Seo.Description}
                </p>
              </div>
            }
          >
            {category.MainInfo.Title}
          </Popover>
        ),
      },

      {
        title: "Source",
        dataIndex: "source_name",
        key: "source_name",
        render: (_, record) => (
          <div className="flex flex-row justify-between">
            <LinkPreview url={record.source_link}>
              <h2>{record.source_name}</h2>
            </LinkPreview>
          </div>
        ),
      },
      {
        title: "Edit",
        dataIndex: "id",
        render: (id: string) => (
          <div className=" w-full flex items-center  justify-center">
            <Link
              to={`/articles/edit-main-info/${id}?page=${page}&pageSize=${pageSize}`}
            >
              <MdEdit />
            </Link>
          </div>
        ),
      },
    ],
  },

  {
    title: "Seo",
    dataIndex: "keywords",
    key: "keywords",
    render: (keywords, record) => (
      <div className="flex flex-row justify-between items-center">
        <Popover title="Description" content={record.description}>
          {keywords.map((keyword: string) => (
            <Tag color="blue" key={keyword}>
              {keyword}
            </Tag>
          ))}
        </Popover>

        <Link
          to={`/articles/edit-seo/${record.id}?page=${page}&pageSize=${pageSize}`}
        >
          <MdEdit />
        </Link>
      </div>
    ),
  },
  {
    title: "Created At",
    dataIndex: "createdAt",
    key: "createdAt",
  },
  {
    title: "Updated At",
    dataIndex: "updatedAt",
    key: "updatedAt",
    render: (updatedAt: string, record) => (
      <div className="flex items-center gap-2">
        <p>
          {updatedAt ? new Date(updatedAt).toLocaleString() : "Not updated yet"}
        </p>
        <Button
          type="text"
          onClick={() => handleUpdate(record.id)}
          size="small"
        >
          <VscActivateBreakpoints />
        </Button>
      </div>
    ),
  },
  {
    title: "Created By",
    dataIndex: "user",
    key: "user",
    render: (user: string, record) => (
      <div className="flex items-center gap-2">
        <p>{user}</p>
      </div>
    ),
  },
  {
    title: "Published",
    dataIndex: "published",
    key: "published",
    render: (published: boolean, record) => (
      <Switch
        checked={published}
        onChange={async (checked) => {
          try {
            if (published) {
              await unpublishArticle(record.id);
            } else {
              await publishArticle(record.id);
            }
            message.success("Article published status updated");
            handleRefresh?.();
          } catch (error) {
            message.error("Failed to update published status");
          }
        }}
      />
    ),
  },
  {
    title: "Actions",
    dataIndex: "actions",
    key: "actions",
    render: (_, record) => (
      <Space>
        <Link to={`/articles/edit-content/${record.id}`}>
          <Button type="primary" size="small" icon={<MdEdit />} />
        </Link>
        <Link
          to={`/articles/edit-image/${record.id}?page=${
            page + 1
          }&pageSize=${pageSize}`}
        >
          <Button size="small" icon={<TbImageInPicture />} />
        </Link>
        <Link
          to={`/articles/manage-attachments/${record.id}?page=${
            page + 1
          }&pageSize=${pageSize}`}
        >
          <Button size="small" icon={<MdAttachFile />} />
        </Link>
        {record.published && !record.onMainNews ? (
          <Popover
            content={
              <div className="flex flex-col gap-2">
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await addToMainNews(record.id, "LARGE");
                      message.success("Article added to main news as LARGE");
                      handleRefresh?.();
                    } catch (error) {
                      message.error("Failed to add to main news");
                    }
                  }}
                >
                  Add as LARGE
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    try {
                      await addToMainNews(record.id, "SMALL");
                      message.success("Article added to main news as SMALL");
                      handleRefresh?.();
                    } catch (error) {
                      message.error("Failed to add to main news");
                    }
                  }}
                >
                  Add as SMALL
                </Button>
              </div>
            }
            title="Add to Main News"
            trigger="click"
          >
            <Button size="small" icon={<MdNewspaper />} />
          </Popover>
        ) : (
          <Popconfirm
            title="Remove from main news?"
            onConfirm={async () => {
              try {
                await removeFromMainNews(record.id);
                message.success("Article removed from main news");
                handleRefresh?.();
              } catch (error) {
                message.error("Failed to remove from main news");
              }
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button size="small" type="primary" icon={<MdNewspaper />} />
          </Popconfirm>
        )}
        <Popconfirm
          title="Are you sure to delete this article?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button danger size="small" icon={<CiTrash />} />
        </Popconfirm>
      </Space>
    ),
  },
];
